import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Phone, SignOut, Check } from "@phosphor-icons/react";

const CONTACT_PHONE = "+91-9970228557";
const CONTACT_PHONE_TEL = "+919970228557";

// Custom Nitisoft-brand palette applied to the reference pricing tiers.
const PLANS = [
  { teams: "02",  price: "Free",       color: "#DC2626", label: "Starter Free" },
  { teams: "04",  price: "Rs. 3000/-", color: "#0EA5E9", label: "Rookie" },
  { teams: "08",  price: "Rs. 4000/-", color: "#F59E0B", label: "Club" },
  { teams: "12",  price: "Rs. 5000/-", color: "#2563EB", label: "League" },
  { teams: "16",  price: "Rs. 6000/-", color: "#0F766E", label: "State" },
  { teams: "22",  price: "Rs. 7000/-", color: "#7C3AED", label: "National" },
  { teams: "30",  price: "Rs. 8000/-", color: "#EA580C", label: "Championship" },
];

function PlanCard({ teams, price, color, label }) {
  return (
    <div
      className="relative rounded-2xl shadow-lg hover:shadow-2xl transition-shadow bg-white overflow-visible min-h-[240px] flex flex-col justify-end"
      data-testid={`pricing-plan-${teams}`}
    >
      <div className="h-16 rounded-t-2xl" style={{ background: color }} />
      <div
        className="absolute left-1/2 top-4 -translate-x-1/2 w-32 h-32 rounded-full bg-white grid place-items-center text-center shadow-lg border-4"
        style={{ borderColor: color }}
      >
        <div>
          {price !== "Free" && <div className="text-xs font-bold text-gray-500">Rs.</div>}
          <div className="font-black text-2xl leading-none" style={{ color }}>{price === "Free" ? "Free" : price.replace(/Rs\.\s?/, "").replace(/\/-/, "")}</div>
          {price !== "Free" && <div className="text-[10px] text-gray-400">/-</div>}
        </div>
      </div>
      <div className="px-6 pt-20 pb-4 text-center">
        <div className="text-sm font-semibold text-gray-500 uppercase tracking-widest">{label}</div>
        <div className="mt-4">
          <div className="text-sm text-gray-500 font-semibold">Up to</div>
          <div className="text-5xl font-black" style={{ color }}>{teams}</div>
          <div className="text-lg text-gray-600 font-semibold">Teams</div>
        </div>
      </div>
      <div className="rounded-b-2xl text-center py-2 text-white text-sm font-bold uppercase tracking-widest" style={{ background: color }}>
        Per Auction
      </div>
    </div>
  );
}

export default function PricingPage() {
  const { user, logout } = useAuth();

  return (
    <div className="bg-white min-h-screen" style={{ fontFamily: "Inter, sans-serif" }} data-testid="pricing-page">
      {/* Top red contact bar */}
      <div className="bg-red-600 text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2 flex items-center justify-between">
          <a href={`tel:${CONTACT_PHONE_TEL}`} className="flex items-center gap-2 font-semibold" data-testid="pricing-phone">
            <Phone size={14} weight="fill" /> {CONTACT_PHONE}
          </a>
          <a href="/pricing" className="hidden sm:inline uppercase tracking-widest text-xs">Pricing &amp; Plans</a>
        </div>
      </div>

      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/nitisoft-logo.png" alt="NitiSoft Auction" className="h-10" />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">NitiSoft</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-widest">Player Auction</span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm font-semibold text-gray-800">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <Link to="/pricing" className="text-blue-600">Pricing</Link>
            <a href="#faq" className="hover:text-blue-600">FAQ</a>
          </div>
          {user ? (
            <button
              type="button"
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-4 py-2 rounded transition-colors flex items-center gap-1"
              data-testid="pricing-logout-btn"
            >
              <SignOut size={14} /> Logout
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/register" className="border-2 border-red-600 text-red-600 hover:bg-red-50 text-sm font-bold px-4 py-2 rounded">Register</Link>
              <Link to="/login" className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-4 py-2 rounded">Login</Link>
            </div>
          )}
        </div>
      </nav>

      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10 text-center">
        <h1 className="text-4xl sm:text-5xl font-black text-gray-900">Simple, per-auction pricing</h1>
        <div className="flex items-center justify-center gap-3 mt-3 mb-4">
          <div className="h-px bg-red-500 flex-1 max-w-xs" />
          <span className="text-red-500 font-bold text-xs uppercase tracking-widest">Choose Your Plan</span>
          <div className="h-px bg-red-500 flex-1 max-w-xs" />
        </div>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Pay only for the tournament you&apos;re running — no monthly commitments, no per-user fees.
          Every plan includes live bidding, LED &amp; stream overlays, team management, reports and Excel import.
        </p>
      </section>

      {/* Plans grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="pricing-grid">
          {PLANS.slice(0, 4).map((p) => (
            <PlanCard key={p.teams} {...p} />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mt-6 max-w-4xl mx-auto">
          {PLANS.slice(4).map((p) => (
            <PlanCard key={p.teams} {...p} />
          ))}
        </div>
      </section>

      {/* Included features */}
      <section id="faq" className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">Everything included, at every price</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 max-w-4xl mx-auto">
          {[
            "Unlimited live-bidding sessions",
            "LED, Stream & Team-Owner overlays",
            "Excel bulk player import",
            "Team & player edit + CSV reports",
            "Audit trail for every action",
            "Broadcast sponsor slot",
          ].map((f) => (
            <div key={f} className="flex items-start gap-2 border border-gray-200 rounded-lg px-4 py-3 bg-white shadow-sm">
              <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" weight="bold" />
              <span className="text-gray-800 font-medium">{f}</span>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link
            to="/register"
            className="inline-block bg-red-600 hover:bg-red-700 text-white text-sm font-bold uppercase tracking-widest px-8 py-3 rounded shadow-md hover:shadow-lg transition-shadow"
            data-testid="pricing-cta-register"
          >
            Start Your First Auction — Free
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-8 text-center">
        <div className="max-w-7xl mx-auto px-4 space-y-2">
          <img src="/nitisoft-logo.png" alt="" className="h-10 mx-auto opacity-90" />
          <div className="text-sm">© {new Date().getFullYear()} NitiSoft Technologies · <a href={`tel:${CONTACT_PHONE_TEL}`} className="hover:text-white">{CONTACT_PHONE}</a></div>
        </div>
      </footer>
    </div>
  );
}
